import React, { memo, useContext, useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import {
  blockExportMetadata,
  blockFactors,
  blockInfoGraphicType,
  blockMetrics,
  blockSettings,
  customizedBlock,
  isReportState,
  viewDataGridSizeType,
} from 'venn-state';

import { useFilteredRequests } from '../../utils/hooks/use-filtered-requests';

import { useAppPrintMode } from '../../../print';
import { DATA_TYPE_EXCEL_STYLES, DataGridSizeType, useResponseParser } from '../../../studio-blocks';

import { getAnalyzedSubjectFromRequestSubject } from 'venn-utils';
import { useSubjectColors } from '../../../studio-blocks/logic/useSubjectColors';
import type { AnalysisProps } from '../../types';

import { ThemeContext } from 'styled-components';
import { GridWrapper } from '../../../studio-blocks/common';
import { ColumnChart } from '../../../studio-blocks/components/charts/returns-distribution';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import { useCommonGridProps } from '../../../studio-blocks/logic/columnUtils';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import { useBlockContext } from '../../contexts/block-context';
import { analysisGridDefaultColDefOverrides } from '../../utils';
import useAnalysisGrid from '../../utils/hooks/use-analysis-grid';

interface SensitivityAnalysisContentProps {
  id: string;
}

export const SensitivityAnalysisContent = ({ id }: SensitivityAnalysisContentProps) => {
  const { inPrintModeOrReportLab } = useAppPrintMode();

  const { requests, analyses } = useFilteredRequests(id);

  const isReport = useRecoilValue(isReportState);
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(id));

  const isCompact = useRecoilValue(viewDataGridSizeType(id)) === DataGridSizeType.COMPACT;
  const exportMetaData = useRecoilValue(blockExportMetadata(id));

  return infoGraphicType === 'GRID' ? (
    <Grid
      id={id}
      requests={requests}
      analyses={analyses}
      inPrintMode={inPrintModeOrReportLab}
      isCompact={isCompact}
      exportMetaData={exportMetaData}
      isReport={isReport}
    />
  ) : infoGraphicType === 'DISTRIBUTE_BAR' ? (
    <Chart
      id={id}
      requests={requests}
      analyses={analyses}
      exportMetaData={exportMetaData}
      inPrintMode={inPrintModeOrReportLab}
    />
  ) : null;
};

const Grid = memo(function InternalGrid({
  id,
  requests,
  analyses: analysesGroup,
  isCompact = false,
  isReport,
  ...otherProps
}: AnalysisProps) {
  const { Colors } = useContext(ThemeContext);

  const selectedBlock = useRecoilValue(customizedBlock(id));
  const { customBlockType } = useRecoilValue(blockSettings(id));
  const selectedMetricIds = useRecoilValue(blockMetrics(id));
  const responseParser = useResponseParser(id);

  const { rowData, columnDefs } = useAnalysisGrid(
    requests,
    responseParser,
    Colors,
    analysesGroup,
    selectedBlock,
    customBlockType,
    selectedMetricIds,
  );

  const commonGridProps = useCommonGridProps(analysisGridDefaultColDefOverrides);

  return (
    <GridWrapper isReport={isReport}>
      <ExportableGrid
        {...otherProps}
        {...commonGridProps}
        selectedRefId={id}
        exportable
        rowData={rowData}
        columnDefs={columnDefs}
        excelStyles={DATA_TYPE_EXCEL_STYLES}
        isCompact={isCompact}
      />
    </GridWrapper>
  );
});

const Chart = memo(function InternalChart({
  id,
  requests,
  analyses,
  inPrintMode,
  exportMetaData,
}: Omit<AnalysisProps, 'isReport'>) {
  const { blockRef: downloadableContentRef } = useBlockContext();

  const analyzedSubjects = useMemo(
    () => requests.map(({ subject }) => getAnalyzedSubjectFromRequestSubject(subject)),
    [requests],
  );
  const subjectColors = useSubjectColors(analyzedSubjects);
  const metrics = useSelectedMetrics();
  const factorMetrics = useRecoilValue(blockFactors(id));
  const selectedBlock = useRecoilValue(customizedBlock(id));
  const { customBlockType } = useRecoilValue(blockSettings(id));
  const responseParser = useResponseParser(id);

  return (
    <ColumnChart
      selectedRefId={id}
      factorMetrics={factorMetrics}
      subjectColors={subjectColors}
      requests={requests}
      analyses={analyses}
      responseParser={responseParser}
      selectedBlock={selectedBlock}
      metrics={metrics}
      customBlockType={customBlockType}
      downloadableContentRef={downloadableContentRef}
      inPrintMode={inPrintMode}
      exportMetaData={exportMetaData}
    />
  );
});
